import React, { useEffect, useState, useRef } from 'react';
import { UploadCloud, Trash2, GripVertical, Loader2, Image as ImageIcon, Eye, EyeOff } from 'lucide-react';
import { adminListLogos, adminUploadLogo, adminUpdateLogo, adminDeleteLogo, adminReorderLogos, backendUrl } from '../../lib/api';
import { toast } from '../../hooks/use-toast';

const LogosTab = () => {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const fileRef = useRef(null);

  const load = async () => setLogos(await adminListLogos());
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setLoading(true);
    try {
      for (const f of files) {
        await adminUploadLogo(f, f.name.split('.')[0]);
      }
      toast({ title: `${files.length} logo${files.length > 1 ? 's' : ''} uploaded` });
      await load();
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this logo?')) return;
    await adminDeleteLogo(id);
    toast({ title: 'Logo removed' });
    load();
  };

  const toggleActive = async (l) => {
    await adminUpdateLogo(l.id, { active: !l.active });
    load();
  };

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    upload(Array.from(e.dataTransfer.files || []));
  };

  // Reorder handlers
  const onCardDragStart = (i) => setDragIdx(i);
  const onCardDragOver = (e) => e.preventDefault();
  const onCardDrop = async (target) => {
    if (dragIdx === null || dragIdx === target) return;
    const next = [...logos];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(target, 0, moved);
    setLogos(next);
    setDragIdx(null);
    try { await adminReorderLogos(next.map(l => l.id)); } catch { /* noop */ }
  };

  return (
    <div>
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`mb-6 rounded-2xl border-2 border-dashed transition-colors cursor-pointer p-8 md:p-12 text-center ${dragOver ? 'border-[#C58B76] bg-[#F4E7DA]' : 'border-[#EFE3D2] bg-white hover:border-[#C58B76]'}`}
      >
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" multiple hidden onChange={(e) => upload(Array.from(e.target.files || []))} />
        <div className="w-14 h-14 mx-auto rounded-full pastel-blush flex items-center justify-center mb-3">
          {loading ? <Loader2 className="w-6 h-6 animate-spin text-[#7A5C4C]" /> : <UploadCloud className="w-6 h-6 text-[#7A5C4C]" />}
        </div>
        <div className="font-serif-display text-xl text-[#2C2416]">Drop client logos here</div>
        <p className="text-sm text-[#7A5C4C] mt-1">or click to browse · PNG, JPG, WEBP, SVG · up to 3 MB each</p>
      </div>

      {logos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#EFE3D2] p-10 text-center">
          <ImageIcon className="w-8 h-8 mx-auto text-[#C58B76]" />
          <p className="mt-3 text-[#5B4A3A]">No client logos yet. Uploaded logos appear in the "Brands that trust us" strip on your website.</p>
        </div>
      ) : (
        <div>
          <p className="text-xs text-[#7A5C4C] mb-3">Drag the cards to reorder. The order here is the display order on the website.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {logos.map((l, i) => (
              <div
                key={l.id}
                draggable
                onDragStart={() => onCardDragStart(i)}
                onDragOver={onCardDragOver}
                onDrop={() => onCardDrop(i)}
                className="bg-white rounded-xl border border-[#EFE3D2] p-3 group cursor-move relative"
              >
                <GripVertical className="absolute top-1.5 left-1.5 w-3.5 h-3.5 text-[#B9A995] opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="h-20 flex items-center justify-center bg-[#FAF6F1] rounded-lg">
                  <img src={backendUrl(l.url)} alt={l.name} className={`max-h-14 max-w-[80%] object-contain ${l.active ? '' : 'opacity-30 grayscale'}`} />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="truncate text-[#2C2416]" title={l.name}>{l.name}</span>
                  <div className="flex gap-1">
                    <button onClick={() => toggleActive(l)} title={l.active ? 'Hide' : 'Show'} className="p-1 text-[#7A5C4C] hover:text-[#C58B76]">
                      {l.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => remove(l.id)} className="p-1 text-[#7A5C4C] hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogosTab;
